import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Smile, Send, Search, ChevronDown, Users2, Megaphone, ArrowLeft, X, CheckCheck, BellRing, ClipboardCheck } from "lucide-react";

const EMOJIS = [
  "👍", "👏", "🙏", "💪", "🔥", "✅", "❗", "⚠️", "📞", "📌",
  "🙂", "😀", "😅", "😉", "😍", "🤔", "😐", "😢", "😡", "🤝",
  "❤️", "🎉", "⭐", "🚀", "⚓", "🏆", "📊", "📝", "⏰", "☕",
];

const fmtTime = (s) => new Date(s).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });

function DirRow({ u, onPick }) {
  if (u.self) {
    return (
      <div className="w-full text-left py-1 text-sm text-muted-foreground" data-testid={`msg-user-${u.id}`}>
        {u.name} <span className="text-[10px]">· {u.role_label} · (siz)</span>
      </div>
    );
  }
  return (
    <button onClick={() => onPick({ kind: "dm", id: u.id, title: u.name })} data-testid={`msg-user-${u.id}`}
      className="w-full text-left py-1 text-sm hover:text-cyan-600">
      {u.name} <span className="text-[10px] text-muted-foreground">· {u.role_label}</span>
    </button>
  );
}
const dayKey = (s) => new Date(s).toDateString();
const dayLabel = (s) => {
  const d = new Date(s);
  const t = new Date();
  const y = new Date(Date.now() - 86400000);
  if (d.toDateString() === t.toDateString()) return "Bugün";
  if (d.toDateString() === y.toDateString()) return "Dün";
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" });
};

export default function MessagesDrawer({ open, onOpenChange, onRead }) {
  const { user } = useAuth();
  const [dir, setDir] = useState(null);
  const [convs, setConvs] = useState([]);
  const [tab, setTab] = useState("chats");
  const [openCom, setOpenCom] = useState(null);
  const [target, setTarget] = useState(null); // {kind, id?, committee_no?, title}
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [emoji, setEmoji] = useState(false);
  const [sending, setSending] = useState(false);
  const [mobileChat, setMobileChat] = useState(false);
  const [find, setFind] = useState("");
  const [findOpen, setFindOpen] = useState(false);
  const [receipts, setReceipts] = useState(null);
  const [reminding, setReminding] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskQ, setTaskQ] = useState("");
  const [taskHits, setTaskHits] = useState([]);
  const [taskPick, setTaskPick] = useState(null);
  const [taskNote, setTaskNote] = useState("");
  const [taskSaving, setTaskSaving] = useState(false);
  const [taskDone, setTaskDone] = useState(null);
  const endRef = useRef(null);

  const loadConvs = async () => {
    try {
      const { data } = await api.get("/messages/conversations");
      setConvs(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!open) return;
    api.get("/messages/directory").then(({ data }) => setDir(data)).catch(() => setDir(null));
    loadConvs();
  }, [open]);

  const loadThread = async (t) => {
    if (!t) return;
    const params = t.kind === "broadcast" ? { broadcast: true }
      : t.kind === "committee" ? { committee_no: t.committee_no } : { peer_id: t.id };
    try {
      const { data } = await api.get("/messages/thread", { params });
      setMsgs(data.messages);
      onRead?.();
      loadConvs();
    } catch { setMsgs([]); }
  };

  useEffect(() => { loadThread(target); /* eslint-disable-next-line */ }, [target?.kind, target?.id, target?.committee_no]);

  useEffect(() => {
    if (!open || !target) return;
    const t = setInterval(() => loadThread(target), 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [open, target?.kind, target?.id, target?.committee_no]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs]);

  const send = async () => {
    const t = text.trim();
    if (!t || !target || sending) return;
    setSending(true);
    try {
      const body = target.kind === "broadcast" ? { broadcast: true, text: t }
        : target.kind === "committee" ? { committee_no: target.committee_no, text: t }
        : { peer_id: target.id, text: t };
      await api.post("/messages", body);
      setText("");
      setEmoji(false);
      await loadThread(target);
    } finally { setSending(false); }
  };

  const openReceipts = async (id) => {
    setReceipts({ loading: true });
    try {
      const { data } = await api.get(`/messages/broadcast/${id}/receipts`);
      setReceipts(data);
    } catch { setReceipts(null); }
  };

  const remind = async () => {
    if (!receipts?.id || reminding) return;
    setReminding(true);
    try {
      const { data } = await api.post(`/messages/broadcast/${receipts.id}/remind`);
      setReceipts((r) => ({ ...r, sent: data.sent }));
      loadConvs();
    } finally { setReminding(false); }
  };

  const searchVoters = async (v) => {
    setTaskQ(v);
    if (v.trim().length < 3) { setTaskHits([]); return; }
    try {
      const { data } = await api.get("/voters", { params: { search: v.trim(), page_size: 8 } });
      setTaskHits((Array.isArray(data) ? data : (data.items || [])).slice(0, 8));
    } catch { setTaskHits([]); }
  };

  const assignTask = async () => {
    if (!taskPick || !target?.id || taskSaving) return;
    setTaskSaving(true);
    try {
      await api.post("/tasks", {
        assignee_id: target.id,
        voter_id: taskPick.id,
        voter_name: taskPick.company_name || taskPick.name || taskQ,
        note: taskNote.trim() || null,
      });
      setTaskDone(`${taskPick.company_name || taskPick.name} · ${target.title}`);
      toast.success(`Görev atandı: ${taskPick.company_name || taskPick.name}`);
      setTaskOpen(false);
      setTaskPick(null); setTaskQ(""); setTaskHits([]); setTaskNote("");
      await loadThread(target);
    } finally { setTaskSaving(false); }
  };

  const pick = (t) => { setTarget(t); setMobileChat(true); };

  const filteredComs = useMemo(() => {
    const list = dir?.committees || [];
    if (!q.trim()) return list;
    const s = q.toLocaleLowerCase("tr");
    return list.filter((c) =>
      String(c.committee_no).includes(s) ||
      (c.name || "").toLocaleLowerCase("tr").includes(s) ||
      c.users.some((u) => (u.name || "").toLocaleLowerCase("tr").includes(s))
    );
  }, [dir, q]);

  const grouped = useMemo(() => {
    const s = find.trim().toLocaleLowerCase("tr");
    const list = s ? msgs.filter((m) => (m.text || "").toLocaleLowerCase("tr").includes(s)) : msgs;
    const out = [];
    let last = null;
    list.forEach((m) => {
      const k = dayKey(m.created_at);
      if (k !== last) { out.push({ sep: dayLabel(m.created_at), id: `sep-${k}` }); last = k; }
      out.push(m);
    });
    return out;
  }, [msgs, find]);

  const foundCount = useMemo(() => grouped.filter((g) => !g.sep).length, [grouped]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden h-[80vh] flex flex-col" data-testid="messages-drawer">
        <div className="flex flex-1 min-h-0">
          {/* Sol: sohbetler / rehber */}
          <div className={`w-full md:w-80 border-r border-border flex flex-col ${mobileChat ? "hidden md:flex" : "flex"}`}>
            <div className="p-3 border-b border-border space-y-2">
              <p className="font-display font-semibold text-sm">Mesajlar</p>
              <div className="inline-flex rounded-lg border border-border bg-muted p-0.5 w-full">
                {[["chats", "Sohbetler"], ["dir", "Rehber"]].map(([k, l]) => (
                  <button key={k} onClick={() => setTab(k)} data-testid={`msg-tab-${k}`}
                    className={`flex-1 px-2 py-1 text-xs font-medium rounded-md transition-colors ${tab === k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>{l}</button>
                ))}
              </div>
              {tab === "dir" && (
                <div className="relative">
                  <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Komite veya kişi ara"
                    className="h-8 pl-8 text-xs" data-testid="msg-search" />
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto">
              {tab === "chats" ? (
                <>
                  {convs.length === 0 && <p className="p-4 text-xs text-muted-foreground" data-testid="msg-no-convs">Henüz sohbet yok. Rehber'den bir kişi seçin.</p>}
                  {convs.map((c) => (
                    <button key={c.conv_id} data-testid={`msg-conv-${c.conv_id}`}
                      onClick={() => pick(c.kind === "dm" ? { kind: "dm", id: c.peer_id, title: c.title }
                        : c.kind === "committee" ? { kind: "committee", committee_no: c.committee_no, title: c.title }
                        : { kind: "broadcast", title: "Duyurular" })}
                      className="w-full text-left px-3 py-2.5 border-b border-border/50 hover:bg-muted/60 transition-colors">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium truncate">{c.title}</span>
                        <span className="text-[10px] text-muted-foreground shrink-0">{fmtTime(c.last_at)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-muted-foreground truncate">{c.kind !== "dm" ? `${c.last_sender}: ` : ""}{c.last_text}</span>
                        {c.unread > 0 && <span className="shrink-0 text-[10px] font-bold text-white bg-emerald-500 rounded-full px-1.5 py-0.5">{c.unread}</span>}
                      </div>
                    </button>
                  ))}
                </>
              ) : (
                <>
                  {user.role === "super_admin" && (
                    <button onClick={() => pick({ kind: "broadcast", title: "Duyurular" })} data-testid="msg-broadcast-btn"
                      className="w-full text-left px-3 py-2.5 border-b border-border/50 hover:bg-muted/60 flex items-center gap-2 text-sm font-medium text-amber-600">
                      <Megaphone className="h-4 w-4" /> Tüm kullanıcılara duyuru
                    </button>
                  )}
                  {(dir?.general || []).length > 0 && (
                    <div className="px-3 py-2 border-b border-border/50">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Genel Yöneticiler</p>
                      {dir.general.map((u) => (
                        <DirRow key={u.id} u={u} onPick={pick} />
                      ))}
                    </div>
                  )}
                  {(dir?.others || []).length > 0 && (
                    <div className="px-3 py-2 border-b border-border/50">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Diğer Yöneticiler</p>
                      {dir.others.map((u) => (
                        <DirRow key={u.id} u={u} onPick={pick} />
                      ))}
                    </div>
                  )}
                  {filteredComs.map((c) => (
                    <div key={c.committee_no} className="border-b border-border/50">
                      <button onClick={() => setOpenCom(openCom === c.committee_no ? null : c.committee_no)}
                        data-testid={`msg-com-${c.committee_no}`}
                        className="w-full px-3 py-2 flex items-center justify-between hover:bg-muted/60 transition-colors">
                        <span className="text-sm truncate"><b>#{c.committee_no}</b> {c.name}</span>
                        <span className="flex items-center gap-1 shrink-0">
                          <span className="text-[10px] text-muted-foreground">{c.users.length} kişi</span>
                          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${openCom === c.committee_no ? "rotate-180" : ""}`} />
                        </span>
                      </button>
                      {openCom === c.committee_no && (
                        <div className="px-3 pb-2 space-y-0.5">
                          <button onClick={() => pick({ kind: "committee", committee_no: c.committee_no, title: `#${c.committee_no} ${c.name} · Komite Sohbeti` })}
                            data-testid={`msg-group-${c.committee_no}`}
                            className="w-full text-left py-1 text-xs text-cyan-600 flex items-center gap-1.5 font-medium">
                            <Users2 className="h-3.5 w-3.5" /> Komite grup sohbeti
                          </button>
                          {c.users.length === 0 && <p className="text-[11px] text-muted-foreground py-1">Bu komitede kullanıcı yok</p>}
                          {c.users.map((u) => (
                            <DirRow key={u.id} u={u} onPick={pick} />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Sağ: sohbet */}
          <div className={`flex-1 flex flex-col min-w-0 ${mobileChat ? "flex" : "hidden md:flex"}`}>
            {!target ? (
              <div className="flex-1 grid place-items-center text-sm text-muted-foreground p-6 text-center" data-testid="msg-empty">
                Soldan bir komite seçip sorumlusuna mesaj gönderin.
              </div>
            ) : (
              <>
                <div className="px-3 py-2.5 border-b border-border flex items-center gap-2 bg-[#0F2C59] text-white">
                  <button className="md:hidden" onClick={() => setMobileChat(false)} data-testid="msg-back"><ArrowLeft className="h-4 w-4" /></button>
                  <p className="text-sm font-semibold truncate flex-1" data-testid="msg-thread-title">{target.title}</p>
                  {target.kind === "dm" && (
                    <button onClick={() => setTaskOpen(true)} className="opacity-80 hover:opacity-100" data-testid="msg-task-btn" title="Arama görevi ata">
                      <ClipboardCheck className="h-4 w-4" />
                    </button>
                  )}
                  <button onClick={() => { setFindOpen((v) => !v); setFind(""); }} className="opacity-80 hover:opacity-100" data-testid="msg-find-toggle" title="Mesajlarda ara">
                    <Search className="h-4 w-4" />
                  </button>
                  <button onClick={() => onOpenChange(false)} className="hidden md:block opacity-70 hover:opacity-100"><X className="h-4 w-4" /></button>
                </div>
                {findOpen && (
                  <div className="px-3 py-2 border-b border-border bg-card flex items-center gap-2">
                    <Input value={find} onChange={(e) => setFind(e.target.value)} autoFocus
                      placeholder="Bu sohbette kelime ara" className="h-8 text-xs" data-testid="msg-find-input" />
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap" data-testid="msg-find-count">
                      {find.trim() ? `${foundCount} sonuç` : `${msgs.length} mesaj`}
                    </span>
                  </div>
                )}
                <div className="flex-1 overflow-y-auto p-3 space-y-1.5 bg-muted/30" data-testid="msg-thread">
                  {msgs.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">Henüz mesaj yok — ilk mesajı siz yazın.</p>}
                  {msgs.length > 0 && find.trim() && foundCount === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-6" data-testid="msg-find-empty">"{find}" için sonuç bulunamadı.</p>
                  )}
                  {grouped.map((m) => m.sep ? (
                    <div key={m.id} className="text-center my-2">
                      <span className="text-[10px] bg-card border border-border rounded-full px-2 py-0.5 text-muted-foreground">{m.sep}</span>
                    </div>
                  ) : (
                    <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`} data-testid={`msg-bubble-${m.id}`}>
                      <div className={`max-w-[75%] rounded-2xl px-3 py-1.5 text-sm shadow-sm ${m.mine ? "bg-emerald-600 text-white rounded-br-sm" : "bg-card border border-border rounded-bl-sm"}`}>
                        {!m.mine && target.kind !== "dm" && <p className="text-[10px] font-semibold text-cyan-600 mb-0.5">{m.sender_name}</p>}
                        <p className="whitespace-pre-wrap break-words">{m.text}</p>
                        <p className={`text-[10px] mt-0.5 text-right ${m.mine ? "text-emerald-100" : "text-muted-foreground"}`}>
                          {fmtTime(m.created_at)}{m.mine && <span className="ml-1">{m.read ? "✓✓" : "✓"}</span>}
                        </p>
                        {m.mine && target.kind === "broadcast" && user.role === "super_admin" && (
                          <button onClick={() => openReceipts(m.id)} data-testid={`msg-receipts-${m.id}`}
                            className="mt-1 text-[10px] font-semibold text-white/90 underline flex items-center gap-1">
                            <CheckCheck className="h-3 w-3" /> Kim okudu?
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={endRef} />
                </div>
                {emoji && (
                  <div className="border-t border-border p-2 grid grid-cols-10 gap-1 bg-card" data-testid="emoji-picker">
                    {EMOJIS.map((e) => (
                      <button key={e} onClick={() => setText((t) => t + e)} className="text-lg hover:scale-125 transition-transform" data-testid={`emoji-${e}`}>{e}</button>
                    ))}
                  </div>
                )}
                <div className="border-t border-border p-2 flex items-end gap-1.5 bg-card">
                  {target.kind === "dm" && target.id === "system" ? (
                    <p className="flex-1 text-center text-xs text-muted-foreground py-2" data-testid="msg-system-readonly">
                      Bu kanal sistem bildirimleri içindir; yanıt gönderilemez.
                    </p>
                  ) : (
                  <>
                  <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => setEmoji((v) => !v)} data-testid="emoji-toggle">
                    <Smile className="h-5 w-5 text-amber-500" />
                  </Button>
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    rows={1}
                    placeholder="Bir mesaj yazın…"
                    className="flex-1 resize-none max-h-24 rounded-2xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500"
                    data-testid="msg-input"
                  />
                  <Button size="icon" className="h-9 w-9 shrink-0 rounded-full bg-emerald-600 hover:bg-emerald-700" disabled={!text.trim() || sending} onClick={send} data-testid="msg-send">
                    <Send className="h-4 w-4" />
                  </Button>
                  </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>

      <Dialog open={taskOpen} onOpenChange={(o) => { setTaskOpen(o); if (!o) { setTaskPick(null); setTaskQ(""); setTaskHits([]); setTaskNote(""); } }}>
        <DialogContent className="max-w-md" data-testid="task-dialog">
          <div className="space-y-3">
            <div>
              <p className="font-display font-semibold text-sm">Arama Görevi Ata</p>
              <p className="text-xs text-muted-foreground">Sorumlu: <b>{target?.title}</b></p>
            </div>
            <Input value={taskQ} onChange={(e) => searchVoters(e.target.value)} placeholder="Firma ara (en az 3 harf)"
              className="h-9 text-sm" data-testid="task-search" />
            {taskHits.length > 0 && (
              <div className="max-h-40 overflow-y-auto border border-border rounded-md divide-y divide-border/60">
                {taskHits.map((v) => (
                  <button key={v.id} onClick={() => setTaskPick(v)} data-testid={`task-hit-${v.id}`}
                    className={`w-full text-left px-2.5 py-1.5 text-xs hover:bg-muted/60 ${taskPick?.id === v.id ? "bg-cyan-500/10 font-semibold" : ""}`}>
                    {v.company_name || v.name}
                    <span className="text-[10px] text-muted-foreground"> · #{v.committee_no}</span>
                  </button>
                ))}
              </div>
            )}
            {taskPick && <p className="text-xs text-cyan-600 font-medium" data-testid="task-picked">Seçilen: {taskPick.company_name || taskPick.name}</p>}
            <Input value={taskNote} onChange={(e) => setTaskNote(e.target.value)} placeholder="Not (isteğe bağlı)"
              className="h-9 text-sm" data-testid="task-note" />
            <Button onClick={assignTask} disabled={!taskPick || taskSaving} className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid="task-assign-btn">
              Görevi Ata ve Mesaj Gönder
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!receipts} onOpenChange={(o) => !o && setReceipts(null)}>
        <DialogContent className="max-w-md" data-testid="receipts-dialog">
          {receipts?.loading ? (
            <p className="text-sm text-muted-foreground">Yükleniyor…</p>
          ) : receipts ? (
            <div className="space-y-3">
              <div>
                <p className="font-display font-semibold text-sm">Duyuru Okundu Bilgisi</p>
                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{receipts.text}</p>
              </div>
              <div className="flex gap-2 text-xs">
                <span className="px-2 py-1 rounded bg-emerald-500/10 text-emerald-600 font-semibold" data-testid="receipts-read-count">
                  Okuyan: {receipts.read.length}/{receipts.total}
                </span>
                <span className="px-2 py-1 rounded bg-red-500/10 text-red-600 font-semibold" data-testid="receipts-unread-count">
                  Okumayan: {receipts.unread.length}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 max-h-56 overflow-y-auto">
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-emerald-600 font-bold mb-1">Okuyanlar</p>
                  {receipts.read.length === 0 && <p className="text-xs text-muted-foreground">—</p>}
                  {receipts.read.map((u) => (
                    <p key={u.id} className="text-xs py-0.5" data-testid={`receipt-read-${u.id}`}>{u.name} <span className="text-[10px] text-muted-foreground">· {u.role_label}</span></p>
                  ))}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-red-600 font-bold mb-1">Okumayanlar</p>
                  {receipts.unread.length === 0 && <p className="text-xs text-muted-foreground">—</p>}
                  {receipts.unread.map((u) => (
                    <p key={u.id} className="text-xs py-0.5" data-testid={`receipt-unread-${u.id}`}>{u.name} <span className="text-[10px] text-muted-foreground">· {u.role_label}</span></p>
                  ))}
                </div>
              </div>
              {receipts.sent != null && (
                <p className="text-xs text-emerald-600 font-medium" data-testid="receipts-sent-info">{receipts.sent} kişiye hatırlatma gönderildi.</p>
              )}
              {receipts.last_reminded_at && receipts.sent == null && (
                <p className="text-[11px] text-muted-foreground">Son hatırlatma: {new Date(receipts.last_reminded_at).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}</p>
              )}
              <Button onClick={remind} disabled={reminding || receipts.unread.length === 0} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="receipts-remind-btn">
                <BellRing className="h-4 w-4 mr-1.5" />
                {receipts.unread.length === 0 ? "Herkes okudu" : `Okumayan ${receipts.unread.length} kişiye hatırlat`}
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
